import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  EyeIcon,
  EyeSlashIcon,
  LightningIcon,
} from "@phosphor-icons/react";

import BrandMark from "../components/BrandMark";
import { useAuth } from "../contexts/AuthContext";
import { formatApiError } from "../lib/api";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email, password);
      nav(loc.state?.from || "/");
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-900">
      {/* Hero half */}
      <div className="relative hidden lg:block">
        <img
          alt=""
          src="https://images.pexels.com/photos/29206495/pexels-photo-29206495.jpeg"
          className="absolute inset-0 h-full w-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-slate-900/70 mix-blend-multiply" />
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-slate-900/30 to-amber-900/30" />
        <div className="relative h-full flex flex-col justify-between p-10 text-white">
          <div className="flex items-center gap-3">
            <BrandMark size={44} />
            <div>
              <div className="font-heading text-base font-bold tracking-tight">
                ZION SOLAR
              </div>
              <div className="text-[11px] uppercase tracking-wider text-amber-200/90 font-semibold">
                Service OS
              </div>
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-amber-300 mb-3 font-semibold">
              Operations control room
            </div>
            <h1 className="font-heading text-4xl lg:text-5xl font-bold leading-[1.05] max-w-md">
              Run service. Track plans. Stay accountable.
            </h1>
            <p className="mt-4 max-w-md text-sm text-slate-300 leading-relaxed">
              Diagnostic visits, recurring monitoring, dispatch, and warranty
              work — all in one place for the team.
            </p>
            <div className="mt-8 flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
              <LightningIcon size={14} weight="fill" className="text-amber-400" />
              Authorized personnel only
            </div>
          </div>
        </div>
      </div>

      {/* Form half */}
      <div className="flex items-center justify-center p-6 sm:p-10 bg-slate-50">
        <form
          onSubmit={submit}
          data-testid="login-form"
          className="w-full max-w-sm bg-white border border-slate-200 rounded-lg p-8 shadow-card"
        >
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
            Sign in
          </div>
          <h2 className="font-heading text-2xl font-bold text-slate-900 tracking-tight mt-2">
            Service OS Login
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Use your team account to continue.
          </p>

          <div className="mt-6 space-y-4">
            <Field label="Email" htmlFor="email">
              <input
                id="email"
                data-testid="login-email-input"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={INPUT}
              />
            </Field>
            <Field label="Password" htmlFor="password">
              <div className="relative">
                <input
                  id="password"
                  data-testid="login-password-input"
                  type={showPwd ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${INPUT} pr-11`}
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  data-testid="login-password-toggle"
                  aria-label={showPwd ? "Hide password" : "Show password"}
                  aria-pressed={showPwd}
                  className="absolute inset-y-0 right-1.5 flex items-center justify-center w-8 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
                >
                  {showPwd ? (
                    <EyeSlashIcon size={18} weight="regular" />
                  ) : (
                    <EyeIcon size={18} weight="regular" />
                  )}
                </button>
              </div>
            </Field>

            {err && (
              <div
                data-testid="login-error"
                className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800"
              >
                {err}
              </div>
            )}

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={busy}
              className="w-full inline-flex items-center justify-center rounded-md bg-slate-900 px-4 py-2.5 text-[13px] font-semibold text-white hover:bg-slate-800 disabled:opacity-60 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
            >
              {busy ? "Signing in…" : "Sign In"}
            </button>
          </div>

          <div className="mt-6 pt-6 border-t border-slate-200 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
            v0.3.0 · phase 3 · ops
          </div>
        </form>
      </div>
    </div>
  );
}

const INPUT =
  "w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-colors";

function Field({ label, htmlFor, children }) {
  return (
    <div>
      <label
        htmlFor={htmlFor}
        className="block text-[11px] uppercase tracking-wider text-slate-600 font-semibold mb-1.5"
      >
        {label}
      </label>
      {children}
    </div>
  );
}
